import { authSlice } from 'stores/auth';
import { petProfilesSlice } from 'stores/pet-profile-new';
import { RootStore } from 'stores/pet-profile-new/types';
import { getCurrentPetProfile } from 'stores/pet-profile-new/utils/get-current-pet-profile';
import { getCustomerPets } from 'stores/pet-profile-new/utils/get-customer-pets';
import { mergePetProfiles } from 'stores/pet-profile-new/utils/merge-pet-profiles';
import { petTypeSlice } from 'stores/pet-type';
import { PetProfile } from 'types/pet-profile';
import { normalizePetProfileProteinPreferences } from 'utils/pet-profile/protein-preferences';
import { create, StateCreator } from 'zustand';
import { createComputed } from 'zustand-computed';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

/**
 * Middleware that wraps set() to ensure a new state reference is always produced.
 * Placed OUTSIDE zustand-computed to intercept its Object.assign-mutated result
 * and create a fresh object reference so Zustand's change detection works correctly.
 */
const immutableSet =
  (f: StateCreator<RootStore>): StateCreator<RootStore> =>
  (set, get, api) => {
    const wrappedSet: typeof set = (partial, replace) => {
      set((state) => {
        const next = typeof partial === 'function' ? partial(state) : partial;
        return next === state ? { ...next } : next;
      }, replace);
    };
    return f(wrappedSet, get, api);
  };

const LEGACY_PET_STORE_KEY = 'pet-profiles-store';

const computed = createComputed(
  (state: RootStore) => {
    const customerPets = getCustomerPets(state);
    const currentPetProfile = getCurrentPetProfile({ state, customerPets });

    return {
      customerPets,
      currentPetProfile,
    };
  },
  {
    shouldRecompute: (prevState, nextState) => {
      return (
        prevState.customerId !== nextState.customerId ||
        prevState.petProfiles !== nextState.petProfiles ||
        prevState.petType !== nextState.petType ||
        prevState.activePetProfile !== nextState.activePetProfile ||
        prevState.profilesPerCustomer !== nextState.profilesPerCustomer
      );
    },
  }
);

export const createStore = ({ name }: { name: string }) => {
  return create<RootStore>()(
    devtools(
      persist(
        immutableSet(
          computed((...args) => ({
            ...petTypeSlice(...args),
            ...petProfilesSlice(...args),
            ...authSlice(...args),
          })) as StateCreator<RootStore>
        ),
        {
          name,
          storage: createJSONStorage(() => localStorage),
          version: 1,
          onRehydrateStorage: () => (state) => state?.setHasHydrated(true),
          // for the users who have legacy structure
          merge: (persistedState, currentState) => {
            const persisted = (persistedState ?? {}) as Partial<RootStore>;
            const store = {
              ...currentState,
              ...persisted,
              petProfiles: Array.isArray(persisted.petProfiles)
                ? persisted.petProfiles.map(normalizePetProfileProteinPreferences)
                : currentState.petProfiles,
            };
            const raw = localStorage.getItem(LEGACY_PET_STORE_KEY);
            if (!raw) return store;

            try {
              const legacyStore = JSON.parse(raw);
              const legacyPets: PetProfile[] = legacyStore.localPets ?? [];
              const hasLegacyPets = Array.isArray(legacyPets) && legacyPets.length > 0;
              const shouldMigrateLegacyPets =
                store.customerId === null || store.customerId === undefined;
              // if nothing is there or in the wrong format just delete it from the storage
              if (!hasLegacyPets || !shouldMigrateLegacyPets) {
                return store;
              }

              // make update only if there is no customer id is saved - meaning it should be old legacy structure
              return legacyPets.reduce(
                (acc, legacyPet) => ({
                  ...acc,
                  ...mergePetProfiles({
                    store: acc,
                    petProfile: normalizePetProfileProteinPreferences(legacyPet),
                  }),
                }),
                { ...store }
              );
            } catch (error) {
              console.error('Failed to migrate legacy pet profiles store', error);
              // in case we could not migrate successfully just a clean up
            } finally {
              localStorage.removeItem(LEGACY_PET_STORE_KEY);
            }
            return store;
          },
          partialize: (state) => ({
            petType: state.petType,
            activePetProfile: state.activePetProfile,
            petProfiles: state.petProfiles,
            profilesPerCustomer: state.profilesPerCustomer,
            customerId: state.customerId,
            isLoggedIn: state.isLoggedIn,
          }),
        }
      ),
      { name: 'RootStore', enabled: process.env.PD_ENV === 'staging' }
    )
  );
};

export const useRootStore = createStore({ name: 'global-store' });
